import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { GradientCard, GradientVariant } from '../ui/GradientCard';

interface FieldStatus {
    title: string;
    message: string;
    color: string;
    icon: LucideIcon;
}

interface FieldStatusWidgetProps {
    status: FieldStatus;
    isDarkMode: boolean;
    onClick?: () => void;
}

export function FieldStatusWidget({ status, isDarkMode, onClick }: FieldStatusWidgetProps) {
    const getVariant = (color: string): GradientVariant => {
        switch (color) {
            case 'orange': return 'critical';
            case 'blue': return 'primary';
            case 'emerald': return 'success';
            default: return 'entity';
        }
    };

    const variant = getVariant(status.color);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mb-6"
        >
            <GradientCard
                variant={variant}
                onClick={onClick}
                className="rounded-3xl"
                innerClassName="rounded-[1.3rem] p-6 text-center"
            >
                <div className="flex items-center justify-center gap-3 mb-3">
                    <status.icon className={`w-6 h-6 ${isDarkMode ? 'text-white' : `text-${status.color}-600`}`} strokeWidth={2.5} />
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Field Status</h3>
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                    <motion.div
                        className={`w-3 h-3 rounded-full ${isDarkMode ? `bg-${status.color}-400` : `bg-${status.color}-500`}`}
                        animate={{
                            scale: [1, 1.2, 1],
                            opacity: [1, 0.7, 1]
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    />
                    <span className={`text-xl font-semibold ${isDarkMode ? 'text-white' : `text-${status.color}-700`}`}>
                        {status.title}
                    </span>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-white/80' : 'text-gray-600'}`}>
                    {status.message}
                </p>
            </GradientCard>
        </motion.div>
    );
}
